import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Progress,
  Empty,
  Tooltip,
  Badge,
  Collapse,
  message,
  Spin,
} from 'antd';
import {
  BulbOutlined,
  PlusOutlined,
  ThunderboltOutlined,
  CheckOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useAppDispatch } from 'store';
import { createSegment } from 'store/slices/push.slice';
import { applicationsApi } from 'api/endpoints/applications.api';
import { AppUser } from 'types/application.types';
import {
  generateSegmentSuggestions,
  SegmentSuggestion,
} from 'utils/segmentSuggestions';

const { Text } = Typography;

interface Props {
  appId: number;
  existingSegmentNames?: string[];
}

const PRIORITY_CONFIG = {
  high: { color: 'red', label: 'Высокий' },
  medium: { color: 'orange', label: 'Средний' },
  low: { color: 'blue', label: 'Низкий' },
};

const TAG_LABELS: Record<string, { color: string; label: string }> = {
  os: { color: 'green', label: 'OS' },
  locale: { color: 'cyan', label: 'Локаль' },
  country: { color: 'geekblue', label: 'Страна' },
  offer: { color: 'purple', label: 'Оффер' },
  conversion: { color: 'gold', label: 'Конверсия' },
  retarget: { color: 'magenta', label: 'Ретаргетинг' },
  combo: { color: 'volcano', label: 'Комбо' },
};

const BATCH_SIZE = 500;

const SegmentSuggestions: React.FC<Props> = ({ appId, existingSegmentNames }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [allUsers, setAllUsers] = useState<AppUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [creatingIds, setCreatingIds] = useState<Set<string>>(new Set());
  const [createdIds, setCreatedIds] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState(false);
  const loadAllUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const collected: AppUser[] = [];
      let offset = 0;
      let hasMore = true;

      while (hasMore) {
        const response = await applicationsApi.listUsers(appId, {
          limit: BATCH_SIZE,
          offset,
        });

        const items: AppUser[] =
          (response as any)?.users ||
          (response as any)?.items ||
          (response as any)?.data ||
          (Array.isArray(response) ? response : []);

        if (!items.length) break;

        collected.push(...items);
        offset += BATCH_SIZE;

        const total = (response as any)?.total ?? 0;
        hasMore = total > 0 ? collected.length < total : items.length === BATCH_SIZE;
      }

      setAllUsers(collected);
      setIsLoaded(true);
    } catch {
      message.error('Не удалось загрузить пользователей для анализа');
    } finally {
      setIsLoadingUsers(false);
    }
  }, [appId]);
  useEffect(() => {
    if (expanded && !isLoaded && !isLoadingUsers) {
      loadAllUsers();
    }
  }, [expanded, isLoaded, isLoadingUsers, loadAllUsers]);

  const suggestions = useMemo(
    () => (isLoaded ? generateSegmentSuggestions(allUsers, existingSegmentNames) : []),
    [allUsers, existingSegmentNames, isLoaded],
  );

  const handleCreateSegment = async (s: SegmentSuggestion) => {
    setCreatingIds(prev => new Set(prev).add(s.id));
    try {
      await dispatch(
        createSegment({
          app_id: appId,
          name: s.name,
          description: s.description,
          conditions: s.conditions,
        }),
      ).unwrap();
      message.success(`Сегмент "${s.name}" создан`);
      setCreatedIds(prev => new Set(prev).add(s.id));
    } catch {
      message.error('Не удалось создать сегмент');
    } finally {
      setCreatingIds(prev => {
        const next = new Set(prev);
        next.delete(s.id);
        return next;
      });
    }
  };

  const handleGoToForm = (s: SegmentSuggestion) => {
    navigate('/segments/new', {
      state: {
        appId,
        prefill: {
          name: s.name,
          description: s.description,
          conditions: s.conditions,
        },
      },
    });
  };

  const renderSuggestion = (s: SegmentSuggestion) => {
    const isCreating = creatingIds.has(s.id);
    const isCreated = createdIds.has(s.id);
    const cfg = PRIORITY_CONFIG[s.priority];
    const borderColor =
      cfg.color === 'red' ? '#ff4d4f' : cfg.color === 'orange' ? '#fa8c16' : '#1890ff';

    return (
      <Card
        key={s.id}
        size="small"
        style={{
          marginBottom: 8,
          borderLeft: `3px solid ${borderColor}`,
          opacity: isCreated ? 0.6 : 1,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, marginRight: 12 }}>
            <Space size={4} style={{ marginBottom: 4 }}>
              <Text strong>{s.name}</Text>
              <Tag color={cfg.color} style={{ margin: 0, fontSize: 11 }}>{cfg.label}</Tag>
            </Space>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>{s.description}</Text>
            </div>
            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Progress
                percent={s.percentage}
                size="small"
                style={{ width: 120, margin: 0 }}
                strokeColor={
                  s.percentage > 50 ? '#52c41a' : s.percentage > 20 ? '#1890ff' : '#fa8c16'
                }
              />
              <Text style={{ fontSize: 12 }}>
                {s.matchedUsers} / {s.totalUsers}
              </Text>
            </div>
            <Space size={4} style={{ marginTop: 4 }}>
              {s.tags.map(tag => {
                const t = TAG_LABELS[tag];
                return t ? (
                  <Tag key={tag} color={t.color} style={{ margin: 0, fontSize: 10 }}>{t.label}</Tag>
                ) : null;
              })}
            </Space>
          </div>

          <Space direction="vertical" size={4}>
            {isCreated ? (
              <Button size="small" disabled icon={<CheckOutlined />}>Создан</Button>
            ) : (
              <>
                <Tooltip title="Создать сегмент сразу">
                  <Button
                    size="small"
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    loading={isCreating}
                    onClick={() => handleCreateSegment(s)}
                  >
                    Создать
                  </Button>
                </Tooltip>
                <Tooltip title="Открыть форму для настройки">
                  <Button
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleGoToForm(s)}
                  >
                    Настроить
                  </Button>
                </Tooltip>
              </>
            )}
          </Space>
        </div>
      </Card>
    );
  };

  const highPriority = suggestions.filter(s => s.priority === 'high');
  const other = suggestions.filter(s => s.priority !== 'high');

  return (
    <Card
      size="small"
      style={{ marginBottom: 16 }}
      title={
        <Space style={{ cursor: 'pointer' }} onClick={() => setExpanded(prev => !prev)}>
          <BulbOutlined style={{ color: '#faad14' }} />
          <Text strong>Предложения по сегментам</Text>
          {isLoaded && suggestions.length > 0 && (
            <Badge count={suggestions.length} style={{ backgroundColor: '#1890ff' }} />
          )}
        </Space>
      }
      extra={
        expanded && isLoaded ? (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={loadAllUsers}
            loading={isLoadingUsers}
          >
            Обновить
          </Button>
        ) : !expanded ? (
          <Button size="small" onClick={() => setExpanded(true)}>
            Показать
          </Button>
        ) : null
      }
    >
      {!expanded ? (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Нажмите «Показать» — система проанализирует всех пользователей и предложит сегменты
        </Text>
      ) : isLoadingUsers ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin tip="Загрузка пользователей для анализа..." />
        </div>
      ) : !suggestions.length ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Недостаточно данных для предложений"
        />
      ) : (
        <>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
            Проанализировано {allUsers.length} пользователей
          </Text>
          {highPriority.length > 0 && (
            <>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
                Рекомендуемые
              </Text>
              {highPriority.map(renderSuggestion)}
            </>
          )}
          {other.length > 0 && (
            <Collapse
              ghost
              items={[
                {
                  key: 'other',
                  label: (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Ещё предложения ({other.length})
                    </Text>
                  ),
                  children: other.map(renderSuggestion),
                },
              ]}
            />
          )}
        </>
      )}
    </Card>
  );
};

export default SegmentSuggestions;